import type { UiText } from '@ory/client';
import { AlertCard, AlertCardVariant } from '@/components/AlertCard';
import ResponseMessage from '@/components/Error/ResponseMessage';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';
type Props = {
  /** Ory Kratosのルートレベルのメッセージ配列 */
  messages: UiText[];
};

/**
 * 画面のタイトル・サブタイトル・ボタン文言と重複する info メッセージ ID。
 * Kratos が flow header にも同じ趣旨の文言を返すため、二重に Alert を出すと
 * ノイズになり「警告かも？」と誤読されるので非表示にする。
 *
 * - 1010003: "Please confirm this action by verifying that it is you."
 * - 1010004: "Please complete the second authentication challenge."
 */
const SUPPRESSED_INFO_IDS = new Set<number>([1010003, 1010004]);

/**
 * Messagesノードコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるルートレベルのメッセージ（ui.messages）をレンダリングします。
 * リクエスト全体に影響するメッセージ（例: リクエストの期限切れ、セッションエラーなど）を表示します。
 *
 * メッセージタイプに応じて適切なスタイルを適用:
 * - `error`: エラーメッセージ（errorCardタイプで表示）
 * - `info`: 情報メッセージ（defaultタイプで表示）
 * - `success`: 成功メッセージ（defaultタイプで表示）
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#ui-message-codes
 */
export function MessagesNode({
  messages
}: Props) {
  const {
    translateUiText
  } = useNodeTranslation();

  // メッセージが空の場合は何もレンダリングしない
  if (!messages || messages.length === 0) {
    return null;
  }

  // メッセージタイプごとにグループ化
  const errorMessages = messages.filter(msg => msg.type === 'error');
  const infoMessages = messages.filter(msg => msg.type === 'info' && !SUPPRESSED_INFO_IDS.has(msg.id));
  const successMessages = messages.filter(msg => msg.type === 'success');
  return <div className="space-y-4">
      {/* エラーメッセージ */}
      {errorMessages.length > 0 && <div className="space-y-2">
          {errorMessages.map((msg, index) => <ResponseMessage key={index} type="errorCard">
              {translateUiText(msg)}
            </ResponseMessage>)}
        </div>}

      {/* 情報メッセージ */}
      {infoMessages.length > 0 && <div className="space-y-2">
          {infoMessages.map((msg, index) => <AlertCard key={index} description={translateUiText(msg)} variant={AlertCardVariant.mutedPrimary} />)}
        </div>}

      {/* 成功メッセージ */}
      {successMessages.length > 0 && <div className="space-y-2">
          {successMessages.map((msg, index) => <AlertCard key={index} description={translateUiText(msg)} variant={AlertCardVariant.mutedPrimary} />)}
        </div>}
    </div>;
}